import React from 'react';
import { getSecureDisplayName, IMMUTABLE_TEXTS } from '../utils/textProtection';

// Composant garde pour protéger les textes contre la corruption
const TextGuard = ({ 
  children, 
  type = 'general', 
  fallback = 'Texte protégé', 
  className = "",
  ...props 
}) => {
  
  const protectText = (text) => {
    if (!text) return fallback;
    
    try {
      // Si c'est un objet React (JSX), extraire le texte
      if (React.isValidElement(text)) {
        return text;
      }
      
      // Si c'est une string, la protéger
      if (typeof text === 'string') {
        return getSecureDisplayName(text, type);
      }
      
      return text;
    } catch (error) {
      console.warn('TextGuard protection error:', error);
      return fallback;
    }
  };

  const protectedChildren = React.Children.map(children, (child) => {
    if (typeof child === 'string') {
      return protectText(child);
    }
    return child;
  });

  return (
    <span 
      className={`text-guard ${className}`}
      spellCheck="false"
      autoCorrect="off"
      autoCapitalize="off"
      contentEditable="false"
      suppressContentEditableWarning={true}
      data-text-protected="true"
      {...props}
    >
      {protectedChildren}
    </span>
  );
};

// Composant spécialisé pour les noms de guichets
export const CounterNameGuard = ({ counter, className = "", ...props }) => {
  const getCounterDisplayName = (counterValue) => {
    const correctedCounter = counterValue?.toLowerCase();
    
    // Mapping explicite pour éviter toute corruption
    switch (correctedCounter) {
      case 'pain':
      case 'peine':
      case 'pains':
        return IMMUTABLE_TEXTS.DISPLAY_PAINS;
      case 'viennoiserie':
        return IMMUTABLE_TEXTS.DISPLAY_VIENNOISERIE;
      case 'gateaux':
      case 'gâteaux':
        return IMMUTABLE_TEXTS.DISPLAY_GATEAUX;
      default:
        console.warn('Unknown counter value:', counterValue);
        return IMMUTABLE_TEXTS.DISPLAY_PAINS;
    }
  };

  return (
    <TextGuard 
      className={className} 
      type="counter"
      fallback={IMMUTABLE_TEXTS.DISPLAY_PAINS}
      {...props}
    >
      {getCounterDisplayName(counter)}
    </TextGuard>
  );
};

// Composant spécialisé pour les sections
export const SectionNameGuard = ({ section, className = "", ...props }) => {
  const getSectionDisplayName = (sectionValue) => {
    const correctedSection = sectionValue?.toLowerCase();
    
    switch (correctedSection) {
      case 'stock':
      case 'action':
      case 'stocks':
        return IMMUTABLE_TEXTS.STOCK;
      case 'production':
      case 'product':
        return IMMUTABLE_TEXTS.PRODUCTION;
      case 'ventes':
      case 'vient':
        return IMMUTABLE_TEXTS.VENTES;
      case 'retours':
      case 'retire':
        return IMMUTABLE_TEXTS.RETOURS;
      case 'depenses':
        return IMMUTABLE_TEXTS.DEPENSES;
      case 'rapports':
        return IMMUTABLE_TEXTS.RAPPORTS;
      case 'gerer':
      case 'gérer':
        return IMMUTABLE_TEXTS.GERER;
      default:
        return sectionValue || 'Section';
    }
  };

  return (
    <TextGuard 
      className={className} 
      type="section"
      fallback="Section"
      {...props}
    >
      {getSectionDisplayName(section)}
    </TextGuard>
  );
};

// Composant pour les noms de produits
export const ProductNameGuard = ({ productId, productName, className = "", ...props }) => {
  const getProductDisplayName = () => {
    // Mapping explicite par ID pour les produits critiques
    const productMap = {
      'c9a756d6-08c1-45e0-9a47-235e3252eb26': IMMUTABLE_TEXTS.PAIN_150F,
      'aea74b0b-e88a-4897-bc63-a510d55a9d2c': IMMUTABLE_TEXTS.PAIN_200F
    };
    
    if (productId && productMap[productId]) {
      return productMap[productId];
    }
    
    // Sinon, nettoyer le nom du produit
    return correctCorruptedText(productName) || 'Produit inconnu';
  };

  return (
    <TextGuard 
      className={className} 
      type="product"
      fallback="Produit inconnu"
      {...props}
    >
      {getProductDisplayName()}
    </TextGuard>
  );
};

export default TextGuard;